'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight, User2Icon } from 'lucide-react';

import { cn } from '../../lib/utils';
import { GLP_SIDEBAR_MOBILE_ATTR, GLPMobileNavDrawer, GLPMobileTopBar } from './glp-mobile-nav';
import { useMobileNavAutoClose } from './use-mobile-nav-auto-close';

export interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  active?: boolean;
  onClick?: () => void;
  /**
   * The module's section links. Only the `responsive` mobile drawer uses
   * them: with two or more, the item opens a drill-down level there. The
   * desktop sidebar ignores them.
   */
  children?: NavItem[];
}

export interface UserProfile {
  name: string;
  initials: string;
  subtitle?: string;
}

interface GLPSidebarContextValue {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
  locked: boolean;
  setLocked: (locked: boolean) => void;
  toggleLocked: () => void;
  /** Whether the mobile navigation drawer (below `lg`) is open. */
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  sidebarRef: React.RefObject<HTMLElement | null>;
}

const GLPSidebarContext = React.createContext<GLPSidebarContextValue | null>(null);

export function useGLPSidebar() {
  const context = React.useContext(GLPSidebarContext);
  if (!context) {
    throw new Error('useGLPSidebar must be used within a GLPSidebarProvider.');
  }
  return context;
}

/**
 * Optional variant — returns null when no GLPSidebarProvider is mounted.
 * Use this from components that want to gracefully degrade (e.g. shared widgets
 * mounted both inside and outside a sidebar context).
 */
export function useOptionalGLPSidebar(): GLPSidebarContextValue | null {
  return React.useContext(GLPSidebarContext);
}

interface GLPSidebarProviderProps {
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  defaultLocked?: boolean;
  locked?: boolean;
  onLockedChange?: (locked: boolean) => void;
}

export function GLPSidebarProvider({
  children,
  defaultCollapsed = false,
  collapsed: collapsedProp,
  onCollapsedChange,
  defaultLocked = false,
  locked: lockedProp,
  onLockedChange,
}: GLPSidebarProviderProps) {
  const [_collapsed, _setCollapsed] = React.useState(defaultCollapsed);
  const [_locked, _setLocked] = React.useState(defaultLocked);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const sidebarRef = React.useRef<HTMLElement | null>(null);

  const collapsed = collapsedProp ?? _collapsed;
  const locked = lockedProp ?? _locked;

  const setCollapsed = React.useCallback(
    (value: boolean) => {
      if (onCollapsedChange) {
        onCollapsedChange(value);
      } else {
        _setCollapsed(value);
      }
    },
    [onCollapsedChange],
  );

  const setLocked = React.useCallback(
    (value: boolean) => {
      if (onLockedChange) {
        onLockedChange(value);
      } else {
        _setLocked(value);
      }
    },
    [onLockedChange],
  );

  const toggleCollapsed = React.useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed]);

  const toggleLocked = React.useCallback(() => {
    setLocked(!locked);
  }, [locked, setLocked]);

  // Auto-close sidebar when clicking outside (unless locked)
  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const closestMenuItem = target.closest('.menu-item');
      const closestLogoContainer = target.closest('.custom-logo-container');
      const closestToggleButton = target.closest('.sidebar-toggle-button');
      // The mobile top bar and drawer are not inside the desktop sidebar;
      // using them must not collapse it.
      if (target.closest(`[${GLP_SIDEBAR_MOBILE_ATTR}]`)) return;

      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(target) &&
        !collapsed &&
        !locked &&
        !closestMenuItem &&
        !closestLogoContainer &&
        !closestToggleButton
      ) {
        setCollapsed(true);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [collapsed, locked, setCollapsed]);

  const contextValue = React.useMemo(
    () => ({
      collapsed,
      setCollapsed,
      toggleCollapsed,
      locked,
      setLocked,
      toggleLocked,
      mobileOpen,
      setMobileOpen,
      sidebarRef,
    }),
    [collapsed, setCollapsed, toggleCollapsed, locked, setLocked, toggleLocked, mobileOpen],
  );

  return <GLPSidebarContext.Provider value={contextValue}>{children}</GLPSidebarContext.Provider>;
}

interface GLPSidebarProps extends React.ComponentProps<'aside'> {
  navItems?: NavItem[];
  /** Simple user profile object — renders the legacy avatar + name strip. */
  user?: UserProfile;
  /**
   * Custom user profile slot. When provided, replaces the simple `user` prop.
   * Use this to pass `<GLPUserProfile />` with a configurable dropdown menu.
   */
  userProfileSlot?: React.ReactNode;
  logo?: React.ReactNode;
  collapsedLogo?: React.ReactNode;
  secondaryNavItems?: NavItem[];
  /**
   * Opt-in mobile layout. Below `lg` (1024px) the sidebar is hidden and a top
   * bar with a menu button opens the nav items in an off-canvas drawer; from
   * `lg` up the sidebar renders exactly as without it. Visibility is switched
   * with CSS, so server-rendered markup matches the client. Off by default,
   * so existing consumers are unchanged.
   */
  responsive?: boolean;
  /** Current route; the mobile drawer closes when it changes. */
  currentPath?: string;
  /** Logo for the mobile top bar and drawer. Defaults to `logo`. */
  mobileLogo?: React.ReactNode;
  /** Right-hand slot of the mobile top bar, e.g. a compact `<GLPUserProfile />`. */
  mobileTopBarActions?: React.ReactNode;
  /** Content under the links in the mobile drawer. */
  mobileDrawerFooter?: React.ReactNode;
}

export function GLPSidebar({
  className,
  navItems = [],
  secondaryNavItems = [],
  user,
  userProfileSlot,
  logo,
  collapsedLogo,
  children,
  responsive = false,
  currentPath,
  mobileLogo,
  mobileTopBarActions,
  mobileDrawerFooter,
  ...props
}: GLPSidebarProps) {
  const { collapsed, toggleCollapsed, sidebarRef, mobileOpen, setMobileOpen } = useGLPSidebar();
  const drawerId = React.useId();
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  const closeMobile = React.useCallback(() => setMobileOpen(false), [setMobileOpen]);

  useMobileNavAutoClose({
    open: responsive && mobileOpen,
    onClose: closeMobile,
    routeKey: currentPath,
  });

  const desktop = (
    <aside
      ref={sidebarRef as React.RefObject<HTMLElement>}
      className={cn(
        'shrink-0 h-full flex flex-col overflow-hidden bg-[hsl(var(--color-primary))] transition-[width] duration-300 ease-in-out',
        responsive && 'hidden lg:flex',
        className,
      )}
      style={{ width: collapsed ? '3.5rem' : '12rem' }}
      {...props}
    >
      {/* Logo & Toggle Header */}
      <div className='flex-shrink-0 pt-6 px-4'>
        {!collapsed ? (
          /* Expanded: Logo and toggle in a row */
          <div className='flex items-center justify-between'>
            <div className='min-w-0 flex-1'>
              {logo ?? (
                <div className='flex flex-col leading-tight'>
                  <span className='text-accessible-text-color font-extrabold text-base tracking-wide'>
                    global link+
                  </span>
                  <span
                    className='text-accessible-text-color/70 font-medium tracking-[0.15em] uppercase'
                    style={{ fontSize: 7 }}
                  >
                    Linking the world of business
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={toggleCollapsed}
              className='sidebar-toggle-button text-accessible-text-color hover:bg-white/10 p-1.5 rounded-lg transition-all duration-200 shrink-0'
              aria-label='Collapse sidebar'
            >
              <ChevronLeft className='h-4 w-4' />
            </button>
          </div>
        ) : (
          /* Collapsed: Logo on top, toggle below */
          <div className='flex flex-col items-center gap-4'>
            <div>
              {collapsedLogo ?? (
                <span className='text-accessible-text-color font-extrabold text-lg'>G+</span>
              )}
            </div>
            <button
              onClick={toggleCollapsed}
              className='sidebar-toggle-button text-accessible-text-color hover:bg-white/10 p-2 rounded-lg transition-all duration-200'
              aria-label='Expand sidebar'
            >
              <ChevronRight className='h-4 w-4' />
            </button>
          </div>
        )}
      </div>

      {/* Scrollable Navigation with Sticky User Profile */}
      <div className='flex-1 min-h-0 flex flex-col overflow-hidden'>
        <nav className='flex-1 min-h-0 overflow-y-auto pl-2 pt-8 flex flex-col'>
          {/* Primary Nav Items */}
          <div className='space-y-4'>
            {navItems.map((item) => (
              <GLPSidebarNavItem key={item.label} item={item} collapsed={collapsed} />
            ))}
            {children}
          </div>

          {/* Spacer to push secondary items to bottom */}
          <div className='flex-1' />

          {/* Secondary Nav Items */}
          {secondaryNavItems.length > 0 && (
            <div className='mt-auto border-gray-400 rounded-l-lg py-2'>
              <div className='space-y-2'>
                {secondaryNavItems.map((item) => (
                  <GLPSidebarNavItem key={item.label} item={item} collapsed={collapsed} />
                ))}
              </div>
            </div>
          )}
        </nav>

        {/* User Profile - Sticky at bottom. `userProfileSlot` (e.g. <GLPUserProfile />) wins
            over the simple `user` strip when provided. */}
        {(userProfileSlot || user) && (
          <div className='flex-shrink-0 p-3 border-t border-accessible-text-color/20 bg-[hsl(var(--color-primary))]'>
            {userProfileSlot ?? (
              <div className='flex items-center gap-2.5'>
                <div className='w-9 h-9 rounded-full bg-accessible-text-color/20 flex items-center justify-center text-accessible-text-color font-bold text-xs shrink-0'>
                  <User2Icon />
                </div>
                {!collapsed && (
                  <div className='min-w-0'>
                    <p className='text-accessible-text-color font-semibold truncate text-sm'>
                      {user!.name}
                    </p>
                    {user!.subtitle && (
                      <p className='text-accessible-text-color/70 text-xs truncate'>
                        {user!.subtitle}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );

  if (!responsive) return desktop;

  return (
    <>
      <GLPMobileTopBar
        open={mobileOpen}
        onToggle={() => setMobileOpen(!mobileOpen)}
        drawerId={drawerId}
        logo={mobileLogo ?? logo}
        actions={mobileTopBarActions}
        menuButtonRef={menuButtonRef}
      />
      <GLPMobileNavDrawer
        open={mobileOpen}
        onOpenChange={setMobileOpen}
        id={drawerId}
        navItems={navItems}
        secondaryNavItems={secondaryNavItems}
        logo={mobileLogo ?? logo}
        footer={mobileDrawerFooter}
        returnFocusRef={menuButtonRef}
      />
      {desktop}
    </>
  );
}

interface GLPSidebarNavItemProps {
  item: NavItem;
  collapsed: boolean;
}

function GLPSidebarNavItem({ item, collapsed }: GLPSidebarNavItemProps) {
  const Icon = item.icon;

  const handleClick = (e: React.MouseEvent) => {
    if (item.onClick) {
      e.preventDefault();
      item.onClick();
    }
  };

  return (
    <a
      href={item.href ?? '#'}
      onClick={handleClick}
      title={item.label}
      className={cn(
        'group flex items-center gap-2 py-2.5 pl-3 pr-0 rounded-l-[25px] transition-all duration-300',
        item.active
          ? 'bg-white text-[hsl(var(--color-primary))] font-semibold'
          : 'text-accessible-text-color hover:bg-white/90 hover:text-[hsl(var(--color-primary))] hover:font-semibold',
      )}
    >
      <span
        className={cn(
          'flex-shrink-0 transition-colors duration-300',
          item.active
            ? 'text-[hsl(var(--color-primary))]'
            : 'text-accessible-text-color group-hover:text-[hsl(var(--color-primary))]',
        )}
      >
        <Icon className='h-5 w-5' />
      </span>
      {!collapsed && (
        <span
          className={cn(
            'text-sm truncate transition-colors duration-300',
            item.active
              ? 'text-[hsl(var(--color-primary))]'
              : 'text-accessible-text-color group-hover:text-[hsl(var(--color-primary))]',
          )}
        >
          {item.label}
        </span>
      )}
    </a>
  );
}

export function GLPSidebarNavGroup({
  label,
  children,
  className,
}: {
  label?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const { collapsed } = useGLPSidebar();

  return (
    <div className={cn('space-y-0.5', className)}>
      {label && !collapsed && (
        <span className='px-2.5 py-1 text-[10px] font-medium text-accessible-text-color/60 uppercase tracking-wider'>
          {label}
        </span>
      )}
      {children}
    </div>
  );
}

export function GLPSidebarItem(props: NavItem) {
  const { collapsed } = useGLPSidebar();
  return <GLPSidebarNavItem item={props} collapsed={collapsed} />;
}
